import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RendererKey_PathwayHtml, RendererKey_PathwayImportErrors, RendererKey_PathwayImportSave } from '@app/pathway-shared';
import { AwaitModules, Component_GenericRenderer, ComponentSync, EditableDBItemV3, LL_H_C, LL_V_L, ModuleFE_Utils } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_Pathway } from '../../../_entity/pathway/ModuleFE_Pathway.js';
import { ModuleFE_PathwayState } from '../../../_entity/pathway-state/ModuleFE_PathwayState.js';
import { ModuleFE_PathwayTransition } from '../../../_entity/pathway-transition/ModuleFE_PathwayTransition.js';
import { Editor_Pathway } from './renderers/Editor_Pathway/Editor_Pathway.js';
import './Manager_Pathway.scss';
import { Presenter_GraphViewWrapper } from '../../Page_PathwayEditor/presenters/Presenter_GraphViewWrapper.js';
import { Editor_PathwayState } from './renderers/Editor_PathwayState/Editor_PathwayState.js';
import { Renderer_PathwayHeader, Renderer_PathwayHeader_Modules } from './renderers/Renderer_PathwayHeader/Renderer_PathwayHeader.js';
import { Editor_PathwayTransition } from './renderers/Editor_PathwayTransition/Editor_PathwayTransition.js';
import { Editor_NewPathway } from './renderers/Editor_NewPathway/Editor_NewPathway.js';
import { Panel_PathwayResolutionAccordion } from './renderers/Panel_PathwayResolutionAccordion/Panel_PathwayResolutionAccordion.js';
import { guardResolutionAccordionUnsaved } from './renderers/shared/resolution-accordion-guard.js';
import { TS_HorizontalWorkspace, TS_Workspace } from '@nu-art/ts-workspace-frontend/index';
import { workspaceKey_ManagerPathwayWorkHub } from './workspace-config.js';
export class Manager_Pathway extends ComponentSync {
    // ######################## Life Cycle ########################
    __onPathwayStateSelected = async (pathwayStateId) => {
        if (!(await guardResolutionAccordionUnsaved(this.state.tabId)))
            return;
        this.props.workHubItem.updateArgs(this.state.tabId, { stateId: pathwayStateId, transitionId: undefined });
    };
    __onPathwayTransitionSelected = async (pathwayTransitionId) => {
        if (!(await guardResolutionAccordionUnsaved(this.state.tabId)))
            return;
        this.props.workHubItem.updateArgs(this.state.tabId, { transitionId: pathwayTransitionId, stateId: undefined });
    };
    deriveStateFromProps(nextProps, state) {
        const pathway = ModuleFE_Pathway.cache.unique(nextProps.renderArgs.itemId);
        state.tabId = nextProps.tabId;
        state.renderArgs = nextProps.renderArgs;
        if (state.renderArgs.readonly)
            state.renderArgs = { ...state.renderArgs, editMode: false };
        if (pathway?._id) {
            state.isNewPathway = false;
            state.pathwayStates = ModuleFE_PathwayState.cache.filter(ps => ps.pathwayIds.includes(pathway._id));
            state.pathwayTransitions = ModuleFE_PathwayTransition.cache.filter(pt => pt.pathwayIds.includes(pathway._id));
        }
        else {
            state.isNewPathway = true;
        }
        return state;
    }
    // ######################## Logic ########################
    // ######################## Render ########################
    render() {
        // New pathway has no map yet — just the editor, full width.
        if (this.state.isNewPathway)
            return _jsx(LL_H_C, { className: 'manager-pathway', children: this.render_Editor() });
        // Existing pathway: two resizable columns (editor | map/resolution) via the workspace separator.
        // Editor stays in the left panel at all times; the resolution function only takes over the right panel.
        return _jsx(TS_Workspace, { className: 'manager-pathway', workspaceKey: workspaceKey_ManagerPathwayWorkHub, renderers: { horizontalSpace: TS_HorizontalWorkspace }, instances: {
                editorPanel: () => this.render_Editor(),
                mapPanel: () => this.render_Graph(),
            } });
    }
    render_Editor = () => {
        if (this.state.isNewPathway)
            return _jsx(Editor_NewPathway, { workHubItem: this.props.workHubItem, newItemData: this.state.renderArgs.newItemData, tabId: this.props.tabId });
        return _jsxs(LL_V_L, { className: 'manager-pathway__editor', children: [_jsx(AwaitModules, { modules: Renderer_PathwayHeader_Modules, children: _jsx(Renderer_PathwayHeader, { workHubItem: this.props.workHubItem, renderArgs: this.state.renderArgs, tabId: this.state.tabId }) }), this.render_EditorContent(), this.renderSlot(RendererKey_PathwayImportSave)] });
    };
    // Generic UI-injection slot. Pathway owns the location; other packages (e.g. data-migration's CSV
    // import) register the component. Guarded because getRenderer throws when nothing is registered.
    // key=`${tabId}:${rendererKey}`: Manager_Pathway is reused across pathway tabs, so re-key per tab to
    // reset the registered component's local UI state when switching previews. The rendererKey suffix
    // keeps sibling slots (e.g. the HTML overlay + errors panel in render_Graph) distinct — sharing a
    // key made React reconcile them as one, so the overlay wouldn't update/clear on tab switch.
    renderSlot = (rendererKey) => {
        if (!ModuleFE_Utils.renderers[rendererKey])
            return null;
        return _jsx(Component_GenericRenderer, { rendererKey: rendererKey, props: { tabId: this.state.tabId, pathwayId: this.state.renderArgs.itemId } }, `${this.state.tabId}:${rendererKey}`);
    };
    render_EditorContent = () => {
        const renderArgs = this.state.renderArgs;
        if (renderArgs.transitionId)
            return _jsx(Editor_PathwayTransition, { workHubItem: this.props.workHubItem, tabId: this.state.tabId, renderArgs: renderArgs });
        if (renderArgs.stateId)
            return _jsx(Editor_PathwayState, { workHubItem: this.props.workHubItem, tabId: this.state.tabId, renderArgs: renderArgs });
        return _jsx(Editor_Pathway, { workHubItem: this.props.workHubItem, tabId: this.state.tabId, renderArgs: renderArgs });
    };
    render_Graph = () => {
        // Editing a state: the accordion of all its resolution functions takes over the map column.
        if (this.state.renderArgs.stateId && this.state.renderArgs.editMode)
            return _jsx(Panel_PathwayResolutionAccordion, { workHubItem: this.props.workHubItem, tabId: this.state.tabId, pathwayId: this.state.renderArgs.itemId, stateId: this.state.renderArgs.stateId });
        const { itemId: pathwayId, stateId, transitionId } = this.state.renderArgs;
        const pathwayState = stateId ? ModuleFE_PathwayState.cache.unique(stateId) : undefined;
        const pathwayTransition = transitionId ? ModuleFE_PathwayTransition.cache.unique(transitionId) : undefined;
        const editableState = pathwayState ? new EditableDBItemV3(pathwayState, ModuleFE_PathwayState) : undefined;
        const editableTransition = pathwayTransition ? new EditableDBItemV3(pathwayTransition, ModuleFE_PathwayTransition) : undefined;
        return _jsxs("div", { className: 'manager-pathway__graph-region', children: [_jsx(Presenter_GraphViewWrapper, { pathwayId: pathwayId, editablePathwayState: editableState, editablePathwayTransition: editableTransition }), this.renderSlot(RendererKey_PathwayHtml), this.renderSlot(RendererKey_PathwayImportErrors)] });
    };
}
//# sourceMappingURL=Manager_Pathway.js.map